/**
 * Fire-and-forget application telemetry for Sweet Spot.
 *
 * The shared telemetry snippet does not expose an API for application events,
 * so scanner diagnostics use the same collector directly. The API resolves the
 * signed-in kiosk or person from the session cookie; client-supplied identity is
 * neither needed nor accepted.
 */

import { API_BASE } from './apiBase';

const MAX_KIND = 16;
const MAX_TYPE = 24;
const MAX_MSG = 1000;
const SID_KEY = 'amp_t_sid';

export type TelemetryEventInput = {
  event: 'error' | 'action';
  kind: string;
  type: string;
  message?: string;
  durationMs?: number;
};

function sessionId(): string | null {
  try {
    return window.sessionStorage.getItem(SID_KEY);
  } catch {
    return null;
  }
}

function send(payload: string): void {
  const url = `${API_BASE}/v1/telemetry/collect`;
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'text/plain;charset=UTF-8' });
      if (navigator.sendBeacon(url, blob)) return;
    }
    void fetch(url, {
      method: 'POST',
      body: payload,
      credentials: 'include',
      keepalive: true,
      mode: 'cors',
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
    }).catch(() => undefined);
  } catch {
    /* telemetry must never interrupt store work */
  }
}

export function reportTelemetryEvent(input: TelemetryEventInput): void {
  try {
    const sid = sessionId();
    send(
      JSON.stringify({
        v: 1,
        site: 'sweetspot',
        ...(sid ? { sid } : {}),
        events: [
          {
            t: input.event,
            kind: input.kind.slice(0, MAX_KIND),
            type: input.type.slice(0, MAX_TYPE),
            ts: Date.now(),
            path: window.location.pathname,
            ...(input.message ? { msg: input.message.slice(0, MAX_MSG) } : {}),
            ...(input.durationMs !== undefined && Number.isFinite(input.durationMs)
              ? { dur: Math.max(0, input.durationMs) }
              : {}),
          },
        ],
      }),
    );
  } catch {
    /* ignore */
  }
}
